import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Select, Space, Table, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import {
  bulkAssignSalesTeams,
  listMissingTeamRecords,
  listSalesTeams,
  type MissingTeamRecord,
} from '@/services/salesTeam.service';
import { SALES_TEAM_ENTITY_TYPES } from '@/constants/salesTeamEntityTypes';

const { Title } = Typography;

function documentLink(entityType: string, id: string): string {
  switch (entityType) {
    case 'Lead':
      return `/sales/leads/${id}`;
    case 'Opportunity':
      return `/crm/opportunities/${id}`;
    case 'Quotation':
      return `/sales/quotations/${id}`;
    case 'SalesOrder':
      return `/sales/sales-orders/${id}`;
    case 'BoqHeader':
      return `/sales/estimations/boq/${id}`;
    case 'TradingRentalEstimation':
      return `/sales/estimations/trading-rental/${id}`;
    case 'OrderReviewChecklist':
      return `/crm/order-review-checklists/${id}`;
    case 'ContractReview':
      return `/crm/contract-reviews/${id}`;
    default:
      return '#';
  }
}

const MissingTeamAssignmentPage: React.FC = () => {
  const qc = useQueryClient();
  const [entityType, setEntityType] = useState<string>('Opportunity');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [assignTeamId, setAssignTeamId] = useState<string | undefined>();

  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(true),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['sales-missing-team-admin', entityType],
    queryFn: () => listMissingTeamRecords(entityType, 100),
  });

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.team_name })),
    [teams]
  );

  const assignMut = useMutation({
    mutationFn: bulkAssignSalesTeams,
    onSuccess: (res) => {
      message.success(`Assigned ${res.updated} record(s)`);
      setSelectedRowKeys([]);
      qc.invalidateQueries({ queryKey: ['sales-missing-team-admin'] });
    },
    onError: (e: Error) => message.error(e.message || 'Assign failed'),
  });

  const onBulkAssign = async () => {
    if (!assignTeamId) {
      message.warning('Select a team to assign');
      return;
    }
    const items = selectedRowKeys.map((key) => ({
      entity_type: entityType,
      entity_id: String(key),
      sales_team_id: assignTeamId,
    }));
    await assignMut.mutateAsync({ items });
  };

  const columns = [
    {
      title: 'Document',
      key: 'document',
      render: (_: unknown, r: MissingTeamRecord) =>
        r.document_number ? (
          <Link to={documentLink(entityType, r.id)}>{r.document_number}</Link>
        ) : (
          r.id
        ),
    },
    { title: 'Customer', dataIndex: 'customer_label', key: 'customer_label', ellipsis: true },
    { title: 'Owner', dataIndex: 'owner_user_id', key: 'owner_user_id', width: 120 },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Suggested team',
      key: 'suggested',
      render: (_: unknown, r: MissingTeamRecord) => r.suggested_team_name || '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Missing sales team assignment</Title>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 220 }}
            value={entityType}
            onChange={(v) => {
              setEntityType(v);
              setSelectedRowKeys([]);
            }}
            options={SALES_TEAM_ENTITY_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <Select
            allowClear
            placeholder="Assign selected to team"
            style={{ width: 240 }}
            options={teamOptions}
            value={assignTeamId}
            onChange={setAssignTeamId}
          />
          <Button
            type="primary"
            disabled={!selectedRowKeys.length}
            loading={assignMut.isPending}
            onClick={onBulkAssign}
          >
            Bulk assign ({selectedRowKeys.length})
          </Button>
          <Link to="/platform/sales-teams">
            <Button>Back to teams</Button>
          </Link>
        </Space>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{ pageSize: 25 }}
        />
      </Card>
    </div>
  );
};

export default MissingTeamAssignmentPage;
